export interface HubspotCompanyRef {
  id: string;
  name: string;
}

export interface HubspotDealListItem {
  id: string;
  hubspotDealId: string;
  name: string;
  amount: string | null; // Decimal serialized as string
  stage: string | null;
  pipeline: string | null;
  closeDate: string | null;
  ownerId: string | null;
  lastSyncAt: string;
  hubspotCompany: HubspotCompanyRef | null;
  _count: { pricerDatecQuotes: number };
  /**
   * Sólo con `openOnly`: la oportunidad está en una etapa cerrada. Sólo aparece
   * si se la buscó por su ID exacto, y no se puede elegir.
   */
  cerrada?: boolean;
}

export interface HubspotDealDetail extends HubspotDealListItem {
  rawProperties: Record<string, string | null>;
  pricerDatecQuotes: Array<{
    id: string;
    code: string;
    name: string | null;
    status: string;
    totalConBoleta: number;
    createdAt: string;
  }>;
}

/** De dónde salió el filtro de abiertas: las etapas reales de HubSpot o las de fábrica. */
export type OpenStageFilterSource = 'pipelines' | 'fallback';

export interface OpportunitiesListResponse {
  data: HubspotDealListItem[];
  total: number;
  page: number;
  pageSize: number;
  /** Sólo viene cuando se pidió `openOnly`. */
  openFilter?: OpenStageFilterSource;
}

export interface OpportunitiesFacets {
  stages: string[];
  pipelines: string[];
  owners: string[];
}

export interface HubspotPipelineStage {
  id: string;
  label: string;
  displayOrder: number;
}

export interface HubspotPipeline {
  id: string;
  label: string;
  displayOrder: number;
  stages: HubspotPipelineStage[];
}

export interface PipelinesResponse {
  data: HubspotPipeline[];
  cached: boolean;
  /** Presente sólo si no se pudo consultar HubSpot por configuración (token ilegible). */
  unavailableReason?: 'config';
}

/** Lo que devuelve GET /api/hubspot-deals/sync-status (la leyenda y «Actualizar ahora»). */
export interface OpportunitiesSyncStatus {
  /** false = la empresa no tiene una integración HubSpot activa. */
  available: boolean;
  lastSuccessAt: string | null;
  lastAttempt: {
    status: 'SUCCESS' | 'PARTIAL' | 'FAILED';
    at: string;
    /** Cuántos registros fallaron en ese intento (con PARTIAL, para la leyenda). */
    failedCount: number;
  } | null;
  /** Una actualización pedida que todavía corre (para seguirla al reabrir la pantalla). */
  runningJobId: string | null;
}

/** Mapa stageId → label legible agregando todos los pipelines del tenant. */
export function buildStageLabelMap(pipelines: HubspotPipeline[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of pipelines) {
    for (const s of p.stages) m.set(s.id, s.label);
  }
  return m;
}

export function buildPipelineLabelMap(pipelines: HubspotPipeline[]): Map<string, string> {
  return new Map(pipelines.map((p) => [p.id, p.label]));
}

export type OpportunitiesSortBy =
  | 'closeDate:asc'
  | 'closeDate:desc'
  | 'amount:asc'
  | 'amount:desc'
  | 'lastSyncAt:desc';

export interface OpportunitiesFilters {
  stage?: string[];
  pipeline?: string[];
  ownerId?: string[];
  hubspotCompanyId?: string;
  search?: string;
  closeDateFrom?: string;
  closeDateTo?: string;
  amountMin?: number;
  amountMax?: number;
  /** Sólo oportunidades en etapas abiertas (lo resuelve el servidor). */
  openOnly?: boolean;
  page: number;
  pageSize: number;
  sortBy: OpportunitiesSortBy;
}

export const DEFAULT_FILTERS: OpportunitiesFilters = {
  page: 1,
  pageSize: 25,
  sortBy: 'closeDate:asc',
};

/**
 * La etapa o el pipeline como se lo muestra: el nombre si se conoce; si no, el valor tal cual salvo que sea un ID
 * numérico de HubSpot (ej. «1333599097»), que no le dice nada a nadie: ahí no se muestra nada.
 */
export function etiquetaSinIdCrudo(
  valor: string | null | undefined,
  etiquetas?: Map<string, string>
): string | null {
  if (!valor) return null;
  const nombre = etiquetas?.get(valor);
  if (nombre) return nombre;
  return /^\d{4,}$/.test(valor) ? null : valor;
}
